import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { EmblaCarouselType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { chunk } from 'lodash';

import { isTabletOrMobileDevice, MarketStatuses, WidthElementNames } from 'constants/app';
import { generalBranding, iconsBranding } from 'constants/branding';
import { MARKET_LINES_CAROUSEL_TILE_CLASS_NAME, RunnersStatuses } from 'constants/markets';
import { NumberOfPairs } from 'constants/marketTypes';
import { useUpdateEffect } from 'hooks/useUpdateEffect';
import { getIsDesktop, getIsMobile } from 'redux/modules/appConfigs/selectors';
import { setIsScrollingToTop } from 'redux/modules/appSettings';
import { getElementWidthByName } from 'redux/modules/appSettings/selectors';
import {
  getMarketPricesFirstKeyLineHandicap,
  getMarketPricesFirstKeyLineId,
  getMarketPricesRunnerStatusBySelectionId,
  getMarketPricesSecondKeyLineHandicap,
  getMarketPricesSecondKeyLineId,
  getMarketPricesStatusById
} from 'redux/modules/marketsPrices/selectors';
import { IMarket, TMarketRunner } from 'types/markets';
import { getLinePlusNumber } from 'utils';
import { getIsSelectionEqualsToKeyLine, getMarketTypes } from 'utils/market';

import styles from './MarketLinesCarousel.module.scss';

type MarketLinesCarouselProps = {
  market: IMarket;
  onToggleShowLines?: () => void;
  showAll?: boolean;
  currentSelectionsLine: number;
  setCurrentSelectionsLine: Dispatch<SetStateAction<number>>;
  isOpeningSoonStatus: boolean;
  isSingleMarketPage?: boolean;
  defaultLineNumber: number;
  keyLineNumber?: number;
  maxWidth?: number;
  withTopPadding?: boolean;
  shouldScrollOnCurrentLineUpdate?: boolean;
};

export default function MarketLinesCarousel({
  market,
  onToggleShowLines,
  showAll,
  currentSelectionsLine,
  setCurrentSelectionsLine,
  isOpeningSoonStatus,
  isSingleMarketPage = false,
  defaultLineNumber,
  keyLineNumber,
  maxWidth,
  withTopPadding,
  shouldScrollOnCurrentLineUpdate
}: MarketLinesCarouselProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const firstKeyLineId = useSelector(getMarketPricesFirstKeyLineId(market.marketId));
  const firstKeyLineHandicap = useSelector(getMarketPricesFirstKeyLineHandicap(market.marketId));
  const secondKeyLineId = useSelector(getMarketPricesSecondKeyLineId(market.marketId));
  const secondKeyLineHandicap = useSelector(getMarketPricesSecondKeyLineHandicap(market.marketId));
  const marketStatus = useSelector(getMarketPricesStatusById(market.marketId));
  const middleSectionWidth = useSelector(getElementWidthByName(WidthElementNames.MIDDLE_SECTION_WITHOUT_SCROLLBAR));
  const isDesktop = useSelector(getIsDesktop);

  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);
  const [isStuck, setIsStuck] = useState(false);
  const [tileMaxWidth, setTileMaxWidth] = useState(90);

  const contentRef = useRef<HTMLDivElement>(null);
  const isDefaultLineScrolled = useRef(false);

  const { group } = NumberOfPairs[market.description.marketType as keyof typeof NumberOfPairs] || NumberOfPairs.default;
  const { isHandicap, isTotalGoals, isAsianHandicap } = getMarketTypes(
    market.description.marketType,
    market.description.bettingType
  );
  const isDisabledAllLinesAndTiles =
    (marketStatus === MarketStatuses.CLOSED && !market.closedDate) ||
    marketStatus === MarketStatuses.SUSPENDED ||
    isOpeningSoonStatus;

  let carouselMaxWidth = isDesktop ? middleSectionWidth : 0;

  if (isSingleMarketPage && isDesktop) {
    carouselMaxWidth -= 20; // 10 + 10 -> right margin and left padding of the carousel
  }

  if (onToggleShowLines && isDesktop) {
    carouselMaxWidth -= 110; // 110 - width of 'All Lines' button (90px) and 20px gap between 'All Lines' and Carousel
  }

  const tiles = useMemo(() => {
    return chunk(market.runners, group).map(linesGroup => {
      let isKeyLine: boolean;
      const isFirstLineEqualKeyLine = getIsSelectionEqualsToKeyLine({
        selectionId: linesGroup[0].selectionId,
        handicap: linesGroup[0].handicap,
        firstKeyLineId,
        firstKeyLineHandicap,
        secondKeyLineId,
        secondKeyLineHandicap
      });

      if (linesGroup.length === 2) {
        const isSecondLineEqualKeyLine = getIsSelectionEqualsToKeyLine({
          selectionId: linesGroup[1].selectionId,
          handicap: linesGroup[1].handicap,
          firstKeyLineId,
          firstKeyLineHandicap,
          secondKeyLineId,
          secondKeyLineHandicap
        });

        isKeyLine = isFirstLineEqualKeyLine && isSecondLineEqualKeyLine;
      } else {
        isKeyLine = isFirstLineEqualKeyLine;
      }

      return {
        label: `${isAsianHandicap ? `${t('market.handicap')} ` : ''}${getLinePlusNumber(
          linesGroup[0].handicap,
          isHandicap && !isTotalGoals ? linesGroup[0].handicap > 0 : !isTotalGoals
        )}`,
        isKeyLine,
        runners: linesGroup
      };
    });
  }, [
    market.runners,
    group,
    firstKeyLineId,
    secondKeyLineId,
    firstKeyLineHandicap,
    secondKeyLineHandicap,
    isHandicap,
    isTotalGoals,
    isAsianHandicap
  ]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
    align: 'center',
    containScroll: 'keepSnaps'
  });

  const onPrevButtonClick = () => {
    if (emblaApi) {
      const currentSlide = emblaApi.selectedScrollSnap();
      const containerWidth = emblaApi.containerNode().clientWidth;
      const halfCountOfFullVisibleSlides = Math.ceil(containerWidth / (tileMaxWidth + 10) / 2); // 10px - gap between tiles, 2 - we need to scroll half of visible tiles

      const slideToScroll = currentSlide - halfCountOfFullVisibleSlides;
      emblaApi.scrollTo(slideToScroll >= 0 ? slideToScroll : 0);
    }
  };

  const onNextButtonClick = () => {
    if (emblaApi) {
      const currentSlide = emblaApi.selectedScrollSnap();
      const containerWidth = emblaApi.containerNode().clientWidth;
      const halfCountOfFullVisibleSlides = Math.ceil(containerWidth / (tileMaxWidth + 10) / 2); // 10px - gap between tiles, 2 - we need to scroll half of visible tiles

      const slideToScroll = currentSlide + halfCountOfFullVisibleSlides;
      emblaApi.scrollTo(slideToScroll <= tiles.length - 1 ? slideToScroll : tiles.length - 1);
    }
  };

  const onSelect = (emblaApiParam: EmblaCarouselType) => {
    const scrollProgress = emblaApiParam.scrollProgress();

    setPrevBtnDisabled(scrollProgress <= 0.001);
    setNextBtnDisabled(scrollProgress >= 0.99);
  };

  const handleClickTile = (index: number) => {
    if (!isDisabledAllLinesAndTiles) {
      if (showAll) {
        dispatch(setIsScrollingToTop(true));
      }

      setCurrentSelectionsLine(index);
      if (emblaApi) {
        emblaApi.scrollTo(index);
      }

      if (showAll && onToggleShowLines) {
        onToggleShowLines();
      }
    }
  };

  const handleToggleShowLines = () => {
    if (showAll) {
      dispatch(setIsScrollingToTop(true));
    }

    const lineToSelect = keyLineNumber ?? defaultLineNumber;

    if (currentSelectionsLine !== lineToSelect && lineToSelect >= 0) {
      setCurrentSelectionsLine(lineToSelect);

      if (emblaApi) {
        emblaApi.scrollTo(lineToSelect, true);
      }
    }

    if (onToggleShowLines) {
      onToggleShowLines();
    }
  };

  /**
   * Scroll to the current line when the line is changed
   */
  useUpdateEffect(() => {
    if (emblaApi && shouldScrollOnCurrentLineUpdate) {
      emblaApi.scrollTo(currentSelectionsLine);
    }
  }, [currentSelectionsLine]);

  useEffect(() => {
    if (emblaApi) {
      onSelect(emblaApi);
      emblaApi.on('reInit', onSelect).on('select', onSelect).on('scroll', onSelect);
    }
  }, [emblaApi]);

  useEffect(() => {
    if (onToggleShowLines && showAll && isDesktop) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsStuck(!entry.isIntersecting);
        },
        {
          threshold: [1],
          rootMargin: '-1px 0px 0px 0px' // triggers just before it sticks
        }
      );

      if (contentRef.current) {
        observer.observe(contentRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }
  }, [onToggleShowLines, showAll, isDesktop]);

  useEffect(() => {
    if (emblaApi && defaultLineNumber >= 0 && !isDefaultLineScrolled.current) {
      emblaApi.scrollTo(defaultLineNumber, true);
      isDefaultLineScrolled.current = true;
    }
  }, [defaultLineNumber, emblaApi]);

  useEffect(() => {
    if (contentRef.current) {
      const tilesElements = Array.from(
        contentRef.current.getElementsByClassName(MARKET_LINES_CAROUSEL_TILE_CLASS_NAME)
      ) as HTMLElement[];
      const newTileMaxWidth = Math.max(
        ...tilesElements.map(tile => {
          const isSelectedTile = tile.classList.contains(generalBranding.SELECTED_SELECTION_TILE);
          const lightningIconWidth = isDesktop ? 12 : 16;
          const extraWidth = isSelectedTile ? lightningIconWidth + 5 : 0; // 5px (gap between icon and text)

          return tile.offsetWidth + extraWidth;
        })
      );

      if (newTileMaxWidth !== tileMaxWidth && tileMaxWidth === 90 && isAsianHandicap) {
        setTileMaxWidth(newTileMaxWidth);
      }
    }
  }, [defaultLineNumber]);

  return (
    <div
      ref={contentRef}
      className={classNames(styles.content, {
        [styles.content__sticky]: onToggleShowLines && showAll && isDesktop,
        [styles.content__sticky__shadow]: onToggleShowLines && showAll && isDesktop && isStuck,
        [styles.content__padding]: isSingleMarketPage && isDesktop,
        [styles.content__noTopPadding]: !isDesktop && !withTopPadding
      })}
    >
      {onToggleShowLines && isDesktop && (
        <button
          onClick={isDisabledAllLinesAndTiles ? undefined : handleToggleShowLines}
          className={classNames(styles.allLines, {
            [styles.allLines__selected]: showAll,
            [generalBranding.SELECTED_SELECTION_TILE]: showAll
          })}
          disabled={isDisabledAllLinesAndTiles}
        >
          {t('market.allLines')}
        </button>
      )}
      <div
        className={styles.carousel}
        style={{
          maxWidth: maxWidth ?? (isDesktop ? carouselMaxWidth : '100dvw')
        }}
      >
        <button
          className={classNames(styles.carousel__nav, styles.carousel__nav__prev, {
            [styles.carousel__nav__notDevice]: !isTabletOrMobileDevice
          })}
          type="button"
          onClick={prevBtnDisabled ? undefined : onPrevButtonClick}
          disabled={prevBtnDisabled}
        >
          <i className="biab_custom-icon-chevron-left-2" />
        </button>

        <div
          className={classNames(styles.carousel__viewport, {
            [styles.carousel__viewport__left]: !prevBtnDisabled,
            [styles.carousel__viewport__right]: !nextBtnDisabled
          })}
          ref={emblaRef}
        >
          <div className={styles.carousel__container}>
            {tiles.map(({ label, isKeyLine, runners }, index) => (
              <CarouselTile
                key={label}
                label={label}
                isKeyLine={isKeyLine}
                runners={runners}
                onClick={() => handleClickTile(index)}
                isSelected={!showAll && currentSelectionsLine === index}
                disabled={isDisabledAllLinesAndTiles}
                marketId={market.marketId}
                width={tileMaxWidth}
                isAsianHandicap={isAsianHandicap}
              />
            ))}
          </div>
        </div>
        <button
          className={classNames(styles.carousel__nav, styles.carousel__nav__next, {
            [styles.carousel__nav__notDevice]: !isTabletOrMobileDevice
          })}
          type="button"
          onClick={nextBtnDisabled ? undefined : onNextButtonClick}
          disabled={nextBtnDisabled}
        >
          <i className="biab_custom-icon-chevron-right-2" />
        </button>
      </div>
    </div>
  );
}

type CarouselTileProps = {
  label: string;
  isKeyLine: boolean;
  runners: TMarketRunner[];
  isSelected: boolean;
  disabled: boolean;
  onClick: () => void;
  marketId: string;
  width: number;
  isAsianHandicap: boolean;
};

function CarouselTile({
  label,
  isKeyLine,
  runners,
  isSelected,
  disabled,
  onClick,
  marketId,
  width,
  isAsianHandicap
}: CarouselTileProps) {
  const firstRunnerStatus = useSelector(
    getMarketPricesRunnerStatusBySelectionId(marketId, runners[0].selectionId, runners[0].handicap)
  );
  const secondRunnerStatus = useSelector(
    getMarketPricesRunnerStatusBySelectionId(marketId, runners[1].selectionId, runners[1].handicap)
  );
  const isMobile = useSelector(getIsMobile);

  const isClosedFirstRunner = !!firstRunnerStatus && firstRunnerStatus !== RunnersStatuses.ACTIVE;
  const isClosedSecondRunner = !!secondRunnerStatus && secondRunnerStatus !== RunnersStatuses.ACTIVE;
  const isClosed = runners.length === 2 ? isClosedFirstRunner && isClosedSecondRunner : isClosedFirstRunner;

  return (
    <button
      className={classNames(MARKET_LINES_CAROUSEL_TILE_CLASS_NAME, styles.tile, {
        [styles.tile__closed]: isClosed && !isSelected,
        [styles.tile__selected]: isSelected,
        [styles.tile__disabled]: disabled,
        [generalBranding.SELECTED_SELECTION_TILE]: isSelected && !disabled
      })}
      style={isAsianHandicap ? { minWidth: width } : undefined}
      onClick={onClick}
    >
      {isKeyLine && (
        <i
          className={classNames('biab_custom-icon-lightning', iconsBranding.TILE_ICON, {
            [styles.mobileLightningIcon]: isMobile
          })}
        />
      )}
      {label}
    </button>
  );
}
